import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSearchParamsToObject } from '../../Services/helpers';
import {liveSearch} from './liveSearch';
import {liveSort} from './liveSort';
import { usePreference } from '../../Data/Preference';

const SearchContext = createContext();

export const useSearchContext = () => {
    return useContext(SearchContext);
};

let debounce = null;

export const Provider = ({ children, onChange, data, hasConditionsFilter, persist, debounceTime = 300 }) => {
    const [sortParams, setSortParams] = usePreference(`sort-${persist}`, null);
    const [searchData, setSearchData] = useState(data ?? {});
    const [searchAndSortData, setSearchAndSortData] = useState({...(data ?? {}), ...(sortParams ?? {})});
    const [searchDebounced, setSearchDebounced] = useState(data ?? {});
    const [searchAndSortDebounced, setSearchAndSortDebounced] = useState({...(data ?? {}), ...(sortParams ?? {})});
    const [hasSearchConditions, setHasSearchConditions] = useState(false);
    // Pull in search criteria from URL
    const [searchParams] = useSearchParams();
    useSearchParamsToObject(searchParams, setSearchData);

    useEffect(() => {
        onChange && onChange(searchData);
        setSearchAndSortData({...searchData, ...sortParams});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchData, sortParams]);

    useEffect(() => {
        if (debounce) {
            clearTimeout(debounce);
        }
        debounce = setTimeout(() => {
            setSearchDebounced(searchData);
            setSearchAndSortDebounced({...searchData, ...sortParams});
        }, debounceTime);

        updateHasSearchConditions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchData, sortParams]);

    const setSearchField = (field, value) => {
        setSearchData({
            ...searchData,
            [field]: value,
        });
    }

    const updateHasSearchConditions = () => {
        let _searchData = hasConditionsFilter ? hasConditionsFilter(searchData) : searchData;

        if (typeof _searchData === 'boolean') {
            setHasSearchConditions(_searchData);
            return;
        }

        const filtered = Object.values(_searchData).filter(v => v !== null && v !== undefined && v !== '');

        setHasSearchConditions(filtered.length > 0);
    }

    const providerValues = {
        searchData,
        searchDebounced,
        setSearchData,
        setSearchField,
        searchAndSortData,
        searchAndSortDebounced,
        sortParams,
        setSortParams,
        hasSearchConditions,
        hasConditionsFilter,
        filteredData: hasConditionsFilter ? hasConditionsFilter(searchData) : searchData,
        filteredDebouncedData: hasConditionsFilter ? hasConditionsFilter(searchDebounced) : searchDebounced,
        liveSearch: liveSearch(searchData?.search ?? null),
        liveSort: liveSort(sortParams),
        hasActiveSearch: searchData ? Object.keys(searchData).filter(v => !!v).length > 0 : false,
    };

    return (
        <SearchContext.Provider value={providerValues}>
            {children}
        </SearchContext.Provider>
    );
};
